import { useEffect, useRef, useState } from 'react';
import {
  Bell,
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  MessageCircle,
  Plus,
  Settings,
} from 'lucide-react';
import shipSvg from '@/assets/ship.svg';
import { prefersReducedMotion, useInView } from './useInView';
import './LaptopMotionDemo.css';

const LOGICAL_WIDTH = 1280;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CREAM_DAYS = new Set([
  1, 2, 3, 4, 5, 13, 14, 15, 16, 17, 18, 19, 27, 28, 30, 31,
]);

type DayFill = 'you' | 'them';

interface LaptopDay {
  day: number;
  fill?: DayFill;
  selected?: boolean;
  pending?: boolean;
}

function buildJanuary2026(): LaptopDay[] {
  return Array.from({ length: 35 }, (_, index) => {
    const day = index - 3;
    if (day < 1 || day > 31) return { day: 0 };
    return {
      day,
      fill: CREAM_DAYS.has(day) ? 'them' : 'you',
      selected: day === 11,
      pending: day === 29,
    };
  });
}

const JANUARY_2026 = buildJanuary2026();

function useScreenScale() {
  const screenRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.34);

  useEffect(() => {
    const node = screenRef.current;
    if (!node) return;

    const update = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) {
        setScale(Number((width / LOGICAL_WIDTH).toFixed(4)));
      }
    };

    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { screenRef, scale };
}

export function LaptopMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.2 });
  const { screenRef, scale } = useScreenScale();
  const [reduced] = useState(prefersReducedMotion);
  const animate = seen && !reduced;

  const rootClass = [
    'motion-laptop-wrap',
    seen ? 'is-seen' : '',
    reduced ? 'is-reduced' : '',
    animate ? 'is-animating' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} ref={ref}>
      <div className='motion-laptop'>
        <div className='motion-laptop__lid'>
          <div className='motion-laptop__notch' aria-hidden='true' />
          <div className='motion-laptop__screen' ref={screenRef}>
            <div
              className='lw-app'
              style={{ transform: `scale(${scale})` }}
            >
              <aside className='lw-sidebar'>
                <div className='lw-brand'>
                  <img src={shipSvg} alt='' className='lw-brand__mark' />
                  <span className='lw-brand__word'>Harbor</span>
                </div>

                <nav className='lw-nav' aria-label='Harbor'>
                  <button type='button' className='lw-nav__item'>
                    <DollarSign
                      className='lw-nav__icon'
                      size={20}
                      strokeWidth={1.9}
                    />
                    <span className='lw-nav__label'>Expenses</span>
                  </button>

                  <button
                    type='button'
                    className='lw-nav__item is-active'
                    aria-current='page'
                  >
                    <span className='lw-nav__pill' aria-hidden='true' />
                    <Calendar
                      className='lw-nav__icon'
                      size={20}
                      strokeWidth={1.9}
                    />
                    <span className='lw-nav__label'>Calendar</span>
                  </button>

                  <button type='button' className='lw-nav__item'>
                    <MessageCircle
                      className='lw-nav__icon'
                      size={20}
                      strokeWidth={1.9}
                    />
                    <span className='lw-nav__label'>Messages</span>
                  </button>

                  <button type='button' className='lw-nav__item'>
                    <Bell
                      className='lw-nav__icon'
                      size={20}
                      strokeWidth={1.9}
                    />
                    <span className='lw-nav__label'>Notifications</span>
                  </button>

                  <button type='button' className='lw-nav__item'>
                    <Settings
                      className='lw-nav__icon'
                      size={20}
                      strokeWidth={1.9}
                    />
                    <span className='lw-nav__label'>Settings</span>
                  </button>
                </nav>
              </aside>

              <main className='lw-main'>
                <header className='lw-header'>
                  <h1>Calendar</h1>
                </header>

                <div className='lw-content'>
                  <div className='lw-legend'>
                    <span className='lw-legend__item'>
                      <i className='lw-legend__dot lw-legend__dot--you' />
                      You have kids
                    </span>
                    <span className='lw-legend__item'>
                      <i className='lw-legend__dot lw-legend__dot--them' />
                      Jake has kids
                    </span>
                    <span className='lw-legend__item'>
                      <i className='lw-legend__dot lw-legend__dot--pending' />
                      Pending
                    </span>
                  </div>

                  <section
                    className='lw-card'
                    aria-label='January 2026 custody calendar'
                  >
                    <div className='lw-monthnav'>
                      <button
                        type='button'
                        className='lw-monthnav__btn'
                        aria-label='Previous month'
                      >
                        <ChevronLeft size={20} strokeWidth={2.3} />
                      </button>
                      <span className='lw-monthnav__label'>January 2026</span>
                      <button
                        type='button'
                        className='lw-monthnav__btn'
                        aria-label='Next month'
                      >
                        <ChevronRight size={20} strokeWidth={2.3} />
                      </button>
                    </div>

                    <div className='lw-cal'>
                      <div className='lw-cal__week'>
                        {WEEKDAYS.map((day) => (
                          <span key={day} className='lw-cal__weekday'>
                            {day}
                          </span>
                        ))}
                      </div>

                      <div className='lw-cal__grid'>
                        {JANUARY_2026.map((cell, index) => {
                          const classes = [
                            'lw-cal__cell',
                            cell.day === 0 ? 'is-empty' : '',
                            cell.fill === 'you' ? 'is-you' : '',
                            cell.fill === 'them' ? 'is-them' : '',
                            cell.selected ? 'is-selected' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');

                          return (
                            <span key={index} className={classes}>
                              {cell.day !== 0 ? (
                                <span className='lw-cal__num'>
                                  {cell.day}
                                </span>
                              ) : null}
                              {cell.pending ? (
                                <i className='lw-cal__dot' />
                              ) : null}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </section>

                  <div className='lw-schedules'>
                    <p className='lw-schedules__label'>SCHEDULES</p>
                    <div className='lw-schedule'>
                      <span className='lw-schedule__icon'>
                        <Calendar size={19} strokeWidth={2} />
                      </span>
                      <div className='lw-schedule__body'>
                        <p className='lw-schedule__name'>Bobby</p>
                        <p className='lw-schedule__sub'>Alternating Weeks</p>
                      </div>
                    </div>
                  </div>
                </div>

                <button type='button' className='lw-fab' aria-label='Add event'>
                  <Plus size={25} strokeWidth={2.4} />
                </button>
              </main>
            </div>
          </div>
        </div>
        <div className='motion-laptop__base' aria-hidden='true' />
      </div>
    </div>
  );
}
